import {
  GoAAppHeader,
  GoAMicrositeHeader,
  GoAAppFooter,
  GoAPageBlock,
  GoAAppFooterMetaSection,
  GoAOneColumnLayout,
  GoAThreeColumnLayout,
  GoAAppHeaderMenu,
  GoASpinner,
  GoAModal,
  GoAButtonGroup,
  GoAButton,
  GoAFileUploadInput,
  GoAIcon,
  GoATooltip,
  GoANotification,
} from '@abgov/react-components';

import Routes from '../app-routes';

import { useEffect, useState } from 'react';
import { useIsDesktop } from '../utils/isDesktop';
import { NotificationComponent } from './notifications/notification-component';
import { AppDispatch, RootState } from '../state/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  getLoginInfoFromJwt,
  isUserLoggedIn,
  logout,
  clearLogoutFlag,
  isAdmin,
} from '../state/slices/authSlice';
import { Route, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { SpinnerComponent } from './spinner-component';
import { hideSpinner } from '../state/slices/spinnerSlice';
import {
  clearCollision,
  CollisionState,
  updateCollision,
} from '../state/slices/collisionSlice';
import { set } from 'lodash';
import { Collision } from '../models/collision';
import ErrorBoundary from './error/error-boundry-component';
import { REACT_ALBERTA_CA_ACCOUNT_URL } from '../state/configuration';
import { setUnauthorizedHandler } from '../services/csr-api-redux';

//console.log(import.meta.env);
const baseUrl = import.meta.env.REACT_APP_APP_URL;
//alert(baseUrl)

const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export function Layout() {
  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );
  const stateAuth = useAppSelector((state: RootState) => state.auth);
  const isDesktop = useIsDesktop();
  const auth = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [isThreeColumn, setIsThreeColumn] = useState(isDesktop);

  const LayoutComponent = GoAOneColumnLayout;

  const threeColumnLayoutProps = {};

  const oneColumnnLayoutProps = {};

  const layoutProps = isThreeColumn
    ? threeColumnLayoutProps
    : oneColumnnLayoutProps;

  const isLoggedIn = isUserLoggedIn(stateAuth);
  const username = stateAuth.auth.access.access
    ? (getLoginInfoFromJwt(stateAuth.auth.access.access)?.name ?? '')
    : '';

  const [showStateUpload, setShowStateUpload] = useState(false);
  const [stateFile, setStateFile] = useState<File | null>(null);
  const [collision, setCollision] = useState<CollisionState>();
  const [uiVersion, setUiVersion] = useState('unknown');

  useEffect(() => {
    dispatch(hideSpinner());
    console.log(
      'RENDER: isLoggedIn =',
      isLoggedIn,
      'username =',
      username,
      'loggedOut =',
      stateAuth.loggedOut,
    );
  }, [stateAuth.auth, stateAuth.loggedOut]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      navigate('/session-expired');
    });
  }, [stateAuth.loggedOut]);

  useEffect(() => {
    if (stateConfiguration.uiVersion) {
      setUiVersion(stateConfiguration.uiVersion);
    }
  }, [
    stateConfiguration,
    stateConfiguration.uiVersion,
    stateConfiguration.csrApiVersion,
  ]);

  const signout = (e: any) => {
    e.preventDefault();

    auth
      .signoutRedirect({
        post_logout_redirect_uri: window.location.origin + '/logout',
      })
      .catch((err) => {
        console.error('OIDC logout failed:', err);
        window.location.href = '/logout'; // Fallback
      });
  };

  const getVersionString = () => {
    return 'UI version: ' + uiVersion;
  };

  const { signoutRedirect } = useAuth();

  useEffect(() => {
    if (stateFile) {
      stateFile.text().then((text) => {
        const collision = JSON.parse(text);
        collision.id = stateCollision.collision.id;
        setCollision(collision);
      });
    }
  }, [stateFile]);

  const saveStateFile = () => {
    setShowStateUpload(false);
    if (collision) {
      dispatch(updateCollision(collision.collision));
    }
  };

  const resetCollision = () => {
    setShowStateUpload(false);

    // dispatch(clearCollision());
    localStorage.removeItem('reduxState');
    const reduxStateKey = 'reduxState';
    if (localStorage.getItem(reduxStateKey) !== null) {
      localStorage.removeItem(reduxStateKey);
    }
  };

  const getReturnUrl = () => {
    if (
      (stateAuth.auth.csrUser && stateAuth.auth.csrUser.is_law) ||
      stateAuth.auth.csrUser?.is_tec ||
      stateAuth.auth.csrUser?.is_law_admin
    ) {
      return undefined;
    }

    return '/';
  };

  return (
    <LayoutComponent {...layoutProps}>
      <GoANotification type="information">
        Report My Collision is only available for collisions occurring in the
        city of Calgary at this time, with provincial expansion to follow.
      </GoANotification>
      <SpinnerComponent />
      <section slot="header">
        <GoAModal
          heading="Upload a state file!"
          calloutVariant="information"
          role="dialog"
          open={showStateUpload}
          actions={
            <div>
              <GoAButtonGroup alignment="end">
                <GoAButton
                  type="secondary"
                  onClick={() => setShowStateUpload(false)}
                >
                  Cancel
                </GoAButton>
                <GoAButton onClick={() => saveStateFile()}>Save</GoAButton>
                <GoAButton onClick={() => resetCollision()} type="tertiary">
                  Clear
                </GoAButton>
              </GoAButtonGroup>
            </div>
          }
        >
          Upload a state file:{' '}
          <GoAFileUploadInput
            variant="button"
            accept="application/json"
            onSelectFile={(file) => setStateFile(file)}
          />
        </GoAModal>
        <div>
          <GoAMicrositeHeader
            type="live"
            version={
              <div>
                <GoATooltip content={getVersionString()}>
                  <GoAIcon
                    theme="outline"
                    type="settings"
                    size="small"
                  ></GoAIcon>
                </GoATooltip>
              </div>
            }
            feedbackUrl="https://github.com/GovAlta/ui-components/issues/new/choose"
          />
        </div>
        <GoAAppHeader url={getReturnUrl()} heading="Report My Collision">
          {isUserLoggedIn(stateAuth) ? (
            <div>
              <GoAAppHeaderMenu leadingIcon="person-circle" heading={username}>
                {stateCollision.collision.isEligible ? (
                  <a href="/dashboard">User Dashboard</a>
                ) : (
                  ''
                )}
                <a href={REACT_ALBERTA_CA_ACCOUNT_URL}>Account Info</a>
                {isAdmin(stateAuth) ? (
                  <a href="/law/dashboard">CPS Dashboard</a>
                ) : (
                  ''
                )}
                <a
                  href="#"
                  onClick={(e) => {
                    signout(e);
                  }}
                >
                  Logout
                </a>
              </GoAAppHeaderMenu>
            </div>
          ) : (
            <div></div>
          )}
        </GoAAppHeader>
      </section>
      <section className="content">
        <main>
          <div className="notification-box">
            <NotificationComponent />
          </div>
          <ErrorBoundary>
            <Routes />
          </ErrorBoundary>
        </main>
      </section>
      <section slot="footer">
        <GoAAppFooter maxContentWidth="100%">
          <GoAAppFooterMetaSection>[ Footer Links ]</GoAAppFooterMetaSection>
        </GoAAppFooter>
      </section>
    </LayoutComponent>
  );
}
export default Layout;
